'use client'

import { useCallback, useRef } from 'react'
import { ANALYTICS_EVENTS } from './events'
import type { AnalyticsLocation } from './params'
import { trackSiteEventWhenReady } from './site-event'

/** How much of the section, or of the screen, has to be covered before it counts as seen. */
const SEEN_SHARE = 0.5
/** How long it has to stay that way. A section a smooth scroll carries past is not read. */
const DWELL_MS = 1_000

/** A callback every 5% of the section, so none of the checks below waits on a stale threshold. */
const THRESHOLD_GRID = Array.from({ length: 21 }, (_, i) => i / 20)

/**
 * The observer only calls back when the visible fraction crosses a threshold.
 * "Fills half the screen" is judged against `window.innerHeight` at callback
 * time, which grows when iOS Safari hides its toolbar mid-scroll, so a single
 * threshold computed at mount can go stale and a section taller than two
 * screens would never be reported. The 5% grid keeps the callbacks coming;
 * the computed step still catches the exact moment on a section so tall that
 * 5% of it is more than half a screen.
 */
function seenThresholds(node: HTMLElement): number[] {
  const height = node.getBoundingClientRect().height
  const fillsHalfScreen = height > 0 ? Math.min(1, (window.innerHeight * SEEN_SHARE) / height) : 1
  return [...new Set([...THRESHOLD_GRID, fillsHalfScreen])].sort((a, b) => a - b)
}

/**
 * Callback ref that reports `site_section_viewed` once per mount — i.e. once per
 * page view — the first time the section stays half on screen for a second.
 * The dwell is what keeps "read this far" honest: a CTA that scrolls to an
 * anchor carries the visitor past every section in between.
 *
 * "Half on screen" alone would never fire for a section taller than two
 * screens (a FAQ or a demo on a phone can be), because at most
 * `viewport / section` of it can ever be visible. Such a section counts once it
 * fills half the screen instead.
 *
 * `trackSiteEventWhenReady`, not `trackSiteEvent`: a section can be on screen at first
 * paint, before the `afterInteractive` GA/Pixel snippets have run, and this
 * event is the denominator every entry's click-through rate is divided by.
 */
export function useSectionViewed(trackingKey: AnalyticsLocation | undefined) {
  const reportedRef = useRef(false)

  return useCallback(
    (node: HTMLElement | null) => {
      if (!node || !trackingKey || reportedRef.current) return
      if (typeof IntersectionObserver === 'undefined') return
      let dwell: ReturnType<typeof setTimeout> | null = null
      const cancelDwell = () => {
        if (dwell !== null) clearTimeout(dwell)
        dwell = null
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (reportedRef.current) return
          // A busy main thread can queue several observations of this one
          // section into a single callback, oldest first; only the latest one
          // says where it is now.
          const latest = entries[entries.length - 1]
          const seen =
            latest !== undefined &&
            latest.isIntersecting &&
            (latest.intersectionRatio >= SEEN_SHARE ||
              latest.intersectionRect.height >= window.innerHeight * SEEN_SHARE)
          if (!seen) {
            cancelDwell()
            return
          }
          if (dwell !== null) return
          dwell = setTimeout(() => {
            dwell = null
            reportedRef.current = true
            observer.disconnect()
            trackSiteEventWhenReady(ANALYTICS_EVENTS.siteSectionViewed, { location: trackingKey })
          }, DWELL_MS)
        },
        { threshold: seenThresholds(node) }
      )
      observer.observe(node)
      return () => {
        cancelDwell()
        observer.disconnect()
      }
    },
    [trackingKey]
  )
}
